import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";

const SCALE_BAR = ({ layer }) => {
  const { map_object } = layer;

  const [currentZoom, setCurrentZoom] = useState(0);
  const [centerLngLat, setCenterLngLat] = useState({ lng: 0, lat: 0 });
  const [copiedType, setCopiedType] = useState(null);

  const containerRef = useRef(null);
  const [isDetached, setIsDetached] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isSnapping, setIsSnapping] = useState(false);

  // FIX: Refs to track rAF handles and pinch state — no re-renders
  const zoomRafRef = useRef(null);
  const isPinching = useRef(false);

  useEffect(() => {
    if (!map_object) return;

    setCurrentZoom(map_object.getZoom());
    const initialCenter = map_object.getCenter();
    setCenterLngLat({ lng: initialCenter.lng, lat: initialCenter.lat });

    // FIX: Throttle zoom updates via requestAnimationFrame
    // Skip updates entirely during pinch gestures to prevent jitter
    const handleZoom = () => {
      if (isPinching.current) return;
      if (zoomRafRef.current) return;
      zoomRafRef.current = requestAnimationFrame(() => {
        setCurrentZoom(map_object.getZoom());
        zoomRafRef.current = null;
      });
    };

    // FIX: Track pinch start/end to suppress mid-gesture re-renders
    const handleTouchStart = (e) => {
      if (e.touches && e.touches.length >= 2) {
        isPinching.current = true;
      }
    };

    const handleTouchEnd = () => {
      if (isPinching.current) {
        isPinching.current = false;
        // Sync zoom once after pinch ends
        setCurrentZoom(map_object.getZoom());
        const center = map_object.getCenter();
        setCenterLngLat({ lng: center.lng, lat: center.lat });
      }
    };

    // 'moveend' was already correct — only fires once after panning stops
    const handleMoveEnd = () => {
      const center = map_object.getCenter();
      setCenterLngLat({ lng: center.lng, lat: center.lat });
    };

    map_object.on("zoom", handleZoom);
    map_object.on("moveend", handleMoveEnd);

    // FIX: Listen on the map canvas element directly for touch events
    const canvas = map_object.getCanvas();
    canvas.addEventListener("touchstart", handleTouchStart, { passive: true });
    canvas.addEventListener("touchend", handleTouchEnd, { passive: true });
    canvas.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      map_object.off("zoom", handleZoom);
      map_object.off("moveend", handleMoveEnd);
      canvas.removeEventListener("touchstart", handleTouchStart);
      canvas.removeEventListener("touchend", handleTouchEnd);
      canvas.removeEventListener("touchcancel", handleTouchEnd);
      if (zoomRafRef.current) {
        cancelAnimationFrame(zoomRafRef.current);
        zoomRafRef.current = null;
      }
    };
  }, [map_object]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    };

    const handleMouseUp = () => setIsDragging(false);

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleCopyLocation = useCallback(
    (type) => {
      let textToCopy = "";
      if (type === "lat") textToCopy = centerLngLat.lat.toFixed(10).toString();
      else if (type === "lng")
        textToCopy = centerLngLat.lng.toFixed(10).toString();
      else if (type === "both")
        textToCopy = `${centerLngLat.lat.toFixed(10)}, ${centerLngLat.lng.toFixed(10)}`;

      navigator.clipboard.writeText(textToCopy).then(() => {
        setCopiedType(type);
        setTimeout(() => setCopiedType(null), 2000);
      });
    },
    [centerLngLat],
  );

  const handleMouseDown = useCallback(
    (e) => {
      if (e.button !== 0 || isSnapping) return;

      let rect;
      if (!isDetached && containerRef.current) {
        rect = containerRef.current.getBoundingClientRect();
        setPosition({ x: rect.left, y: rect.top });
        setIsDetached(true);
      } else {
        rect = { left: position.x, top: position.y };
      }

      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    },
    [isSnapping, isDetached, position],
  );

  const handleResetPosition = useCallback(
    (e) => {
      e.stopPropagation();
      if (!containerRef.current || !isDetached) return;

      setIsSnapping(true);
      setIsDragging(false);

      const rect = containerRef.current.getBoundingClientRect();
      const targetX = window.innerWidth - rect.width - 10;
      const targetY = window.innerHeight - rect.height - 10;

      setPosition({ x: targetX, y: targetY });

      setTimeout(() => {
        setIsDetached(false);
        setIsSnapping(false);
      }, 350);
    },
    [isDetached],
  );

  // FIX: Memoize heavy scale calculation — only recomputes when zoom or lat changes
  const scaleData = useMemo(() => {
    const y = centerLngLat.lat * (Math.PI / 180);
    const circumference = 40075016.686;
    const metersPerPixel =
      (circumference * Math.cos(y)) / (512 * Math.pow(2, currentZoom));

    const maxWidthPx = 90;
    const maxMeters = maxWidthPx * metersPerPixel;

    const possibleScales = [
      { d: 40000000, seg: 4 },
      { d: 20000000, seg: 2 },
      { d: 10000000, seg: 2 },
      { d: 4000000, seg: 4 },
      { d: 2000000, seg: 2 },
      { d: 1000000, seg: 2 },
      { d: 400000, seg: 4 },
      { d: 200000, seg: 2 },
      { d: 100000, seg: 2 },
      { d: 40000, seg: 4 },
      { d: 20000, seg: 2 },
      { d: 10000, seg: 2 },
      { d: 4000, seg: 4 },
      { d: 2000, seg: 2 },
      { d: 1000, seg: 2 },
      { d: 400, seg: 4 },
      { d: 200, seg: 2 },
      { d: 100, seg: 2 },
      { d: 40, seg: 4 },
      { d: 20, seg: 2 },
      { d: 10, seg: 2 },
      { d: 4, seg: 4 },
      { d: 2, seg: 2 },
      { d: 1, seg: 2 },
      { d: 0.4, seg: 4 },
      { d: 0.2, seg: 2 },
      { d: 0.1, seg: 2 },
    ];

    const selectedScale = possibleScales.find((s) => maxMeters >= s.d) || {
      d: 1,
      seg: 2,
    };

    const distanceMeters = selectedScale.d;
    const numSegments = selectedScale.seg;
    const widthPx = distanceMeters / metersPerPixel;

    const isKm = distanceMeters >= 1000;
    const unit = isKm ? "km" : "m";
    const val = isKm ? distanceMeters / 1000 : distanceMeters;

    const formatNum = (num) => Number(num.toFixed(2));

    const labels = ["0"];
    for (let i = 1; i <= numSegments; i++) {
      const segmentVal = val * (i / numSegments);
      labels.push(formatNum(segmentVal));
    }

    return { width: widthPx, labels, numSegments, unit };
  }, [centerLngLat.lat, currentZoom]); // FIX: Only lat affects scale, not lng

  return (
    <div
      id="scale_bar_ui"
      ref={containerRef}
      onMouseDown={handleMouseDown}
      // FIX: Stop touch propagation so this UI doesn't interfere with MapLibre's touch handlers
      onTouchStart={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        ...(isDetached
          ? { left: `${position.x}px`, top: `${position.y}px` }
          : { bottom: "10px", right: "10px" }),
        display: "flex",
        alignItems: "flex-end",
        gap: "10px",
        zIndex: 1,
        transition: isSnapping
          ? "left 0.35s ease-in-out, top 0.35s ease-in-out"
          : "none",
        cursor: isDragging ? "grabbing" : isSnapping ? "default" : "grab",
        userSelect: "none",
        // FIX: Promote to its own composite layer — prevents the UI from
        // being redrawn when the map canvas below it repaints
        willChange: "transform",
        transform: "translateZ(0)",
      }}
    >
      {isDetached && !isSnapping && (
        <div
          onMouseDown={(e) => e.stopPropagation()}
          onClick={handleResetPosition}
          title="Reset to fixed position"
          style={{
            position: "absolute",
            top: "-22px",
            right: "0px",
            background: "#fff",
            borderRadius: "50%",
            width: "20px",
            height: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
            border: "1px solid #e0e0e0",
          }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#000"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          marginBottom: "2px",
          marginRight: "10px",
        }}
      >
        <div
          style={{
            position: "relative",
            display: "flex",
            width: `${scaleData.width}px`,
            fontSize: "9px",
            fontWeight: "bold",
            marginBottom: "3px",
            fontFamily: "sans-serif",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              bottom: 0,
              color: "#000",
              textShadow:
                "1px 1px 0px #fff, -1px -1px 0px #fff, 1px -1px 0px #fff, -1px 1px 0px #fff",
            }}
          >
            {scaleData.labels[0]}
          </div>
          {scaleData.labels.slice(1).map((lbl, idx, arr) => {
            const isLast = idx === arr.length - 1;
            const isCrowded = scaleData.width < 80;
            const hideLabel = isCrowded && !isLast;
            return (
              <div
                key={idx}
                style={{
                  flex: 1,
                  textAlign: "right",
                  whiteSpace: "nowrap",
                  color: hideLabel ? "transparent" : "#000",
                  textShadow: hideLabel
                    ? "none"
                    : "1px 1px 0px #fff, -1px -1px 0px #fff, 1px -1px 0px #fff, -1px 1px 0px #fff",
                }}
              >
                {lbl}
                {isLast && (
                  <span
                    style={{
                      position: "absolute",
                      marginLeft: "3px",
                      color: "#000",
                      textShadow:
                        "1px 1px 0px #fff, -1px -1px 0px #fff, 1px -1px 0px #fff, -1px 1px 0px #fff",
                    }}
                  >
                    {scaleData.unit}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <div
          style={{
            display: "flex",
            width: `${scaleData.width}px`,
            height: "6px",
            border: "1px solid #000",
            outline: "1px solid #fff",
            boxSizing: "border-box",
            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
          }}
        >
          {Array.from({ length: scaleData.numSegments }).map((_, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                backgroundColor: i % 2 === 0 ? "#000" : "#fff",
              }}
            />
          ))}
        </div>
      </div>

      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          backgroundColor: "#ffffff",
          borderRadius: "50px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          fontSize: "8px",
          overflow: "hidden",
          border: "1px solid #e0e0e0",
          height: "fit-content",
          cursor: "default",
          alignItems: "stretch",
        }}
      >
        <div
          onClick={() => handleCopyLocation("lat")}
          title="Click to copy latitude"
          style={{
            display: "block",
            padding: "4px 8px",
            cursor: "pointer",
            borderRight: "1px solid #e0e0e0",
            transition: "background-color 0.2s",
            whiteSpace: "nowrap",
            width: "70px",
            overflow: "hidden",
            lineHeight: "12px",
          }}
        >
          {copiedType === "lat"
            ? "Copied!"
            : `Lat: ${centerLngLat.lat.toFixed(10)}`}
        </div>
        <div
          onClick={() => handleCopyLocation("lng")}
          title="Click to copy longitude"
          style={{
            display: "block",
            padding: "4px 8px",
            cursor: "pointer",
            borderRight: "1px solid #e0e0e0",
            transition: "background-color 0.2s",
            whiteSpace: "nowrap",
            width: "70px",
            overflow: "hidden",
            lineHeight: "12px",
          }}
        >
          {copiedType === "lng"
            ? "Copied!"
            : `Long: ${centerLngLat.lng.toFixed(10)}`}
        </div>
        <div
          onClick={() => handleCopyLocation("both")}
          title="Click to copy both coordinates"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "4px 8px",
            cursor: "pointer",
            transition: "all 0.2s",
            backgroundColor: copiedType === "both" ? "#f0fff0" : "transparent",
          }}
        >
          {copiedType === "both" ? (
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#4caf50"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          ) : (
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#666"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          )}
        </div>
      </div>
    </div>
  );
};

export default SCALE_BAR;
